import { faCircle, faDotCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState, useContext } from 'react';
import { Button, Card, CardBody, CardHeader, Input, FormFeedback, Label, FormGroup } from 'reactstrap';
import { useAuth0 } from '../../../../auth0/reactAuth0Spa';
import { toast } from 'react-toastify';
import { checkLoginForError, formatBearerToken, roundToDecimalPlace } from '../../../../Common.functions';
import { fetchDataAgnostic, postDataAgnostic, putData } from '../../../../Services/dataApi';
import style from './AdminEvaluationSettingsLanding.style';
import AdminEvaluationQuestionTemplate from './AdminEvaluationQuestionTemplate/AdminEvaluationQuestionTemplate';
import {
  defaultLikert5QuestionState,
  questionScaleKeys,
  defaultLikert10QuestionScale,
  defaultDreyfus10QuestionScale,
} from './AdminEvaluationSettingsConstants';
import { useHistory } from 'react-router-dom';
import { DepartmentContext } from '../../../../DepartmentWrapper';
import { clone } from '../../../../Services/schedule';
import Swal from 'sweetalert2';
import Focuses from '../AdminSettings/Focuses/Focuses';

let updateTimeout = null;

const AdminEvluationSettingsLanding = ({ setPageChanges, pageChanges, updateChanges, windowWidth }) => {
  const [questionScale, setQuestionScale] = useState(questionScaleKeys.likert5);
  const [scaleData, setScaleData] = useState(questionScaleKeys.likert5);
  const [questionChoices, setQuestionChoices] = useState([]);
  const [editableQuestionChoices, setEditableQuestionChoices] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const [PassingScore, setPassingScore] = useState(0);
  const [maxPassingScore, setMaxPassingScore] = useState(0); // editableQuestionChoices.length
  const [rawSeasonSettings, setRawSeasonSettings] = useState({});

  const [season, setSeason] = useState({});

  const [prescreenState, setPrescreenState] = useState(false); // if Allow prescreen
  const [prescreenMode, setPrescreenMode] = useState(false); // if EnablePrescreenMode (the header dropdown thing)
  const [enablePreScreenQuestionaire, setEnablePreScreenQuestionaire] = useState(false);
  const [enablePrescreenResultsToEvaluators, setEnablePrescreenResultsToEvaluators] = useState(false);
  const [enableEvaluationQuestionaire, setEnableEvaluationQuestionaire] = useState(false);

  const [hasChange, setHasChange] = useState(false);
  const [questionsHaveChange, setQuestionsHaveChange] = useState(false);
  const [scaleHasChange, setScaleHasChange] = useState(false);

  const [isUpdatingPreScreenState, setIsUpdatingPreScreenState] = useState(false);
  const [isUpdatingQuestionnaire, setIsUpdatingQuestionnaires] = useState(false);
  const [isUpdatingOptions, setIsUpdatingOptions] = useState(false);
  const [isUpdatingScaleType, setIsUpdatingScaleType] = useState(false);

  const dContext = useContext(DepartmentContext);
  const { department } = dContext || {};
  const { ProgramType } = department || {};
  const { getTokenSilently, loginWithRedirect } = useAuth0();
  // Alert functionality replaced with toast
  const widthToSwitchToMobile = 1100;

  useEffect(() => {
    getScaleData();
    getSeasonSettings();
  }, []);

  useEffect(() => {
    if (JSON.stringify(editableQuestionChoices) !== JSON.stringify(questionChoices)) {
      setQuestionsHaveChange(true);
    } else {
      setQuestionsHaveChange(false);
    }
  }, [editableQuestionChoices]);

  useEffect(() => {
    setPageChanges({
      evaluation: hasChange,
    });
  }, [hasChange]);

  useEffect(() => {
    if (initialized) {
      if (scaleData != questionScale) {
        setScaleHasChange(true);
      } else {
        setScaleHasChange(false);
      }
    }
  }, [questionScale, scaleData]);

  useEffect(() => {
    if (editableQuestionChoices) {
      setMaxPassingScore(editableQuestionChoices.length);
    }
  }, [editableQuestionChoices]);

  const updateLocalBooleans = (seasonData) => {
    const {
      AllowPrescreen,
      EnableEvaluationQuestionnaire,
      EnablePrescreenQuestionnaire,
      EnablePrescreenMode,
      EnablePrescreenVisibleToAllEvaluators,
    } = seasonData;
    setPrescreenState(AllowPrescreen);
    setEnableEvaluationQuestionaire(EnableEvaluationQuestionnaire);
    setEnablePreScreenQuestionaire(EnablePrescreenQuestionnaire);
    setPrescreenMode(EnablePrescreenMode);
    setEnablePrescreenResultsToEvaluators(EnablePrescreenVisibleToAllEvaluators);

    const newSeason = clone(dContext.season);
    newSeason.AllowPrescreen = AllowPrescreen;
    newSeason.EnableEvaluationQuestionnaire = EnableEvaluationQuestionnaire;
    newSeason.EnablePrescreenQuestionnaire = EnablePrescreenQuestionnaire;
    newSeason.EnablePrescreenMode = EnablePrescreenMode;
    newSeason.EnablePrescreenVisibleToAllEvaluators = EnablePrescreenVisibleToAllEvaluators;

    dContext.setSeason(newSeason);
  };

  const getSeasonSettings = () => {
    getTokenSilently()
      .then((token) => {
        fetchDataAgnostic(
          'department/season',
          {
            pk_Department: dContext.department.pk_Department,
            pk_Season: dContext.season.pk_Season,
          },
          formatBearerToken(token),
        )
          .then((res) => {
            setSeason(res.data);
            updateLocalBooleans(res.data);
            setRawSeasonSettings(res.data);
            setPassingScore(res.data.PassingScore || 0);
            setInitialized(true);
          })
          .catch((err) => {});
      })
      .catch((err) => {
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const getScaleData = () => {
    setIsUpdatingOptions(true);
    getTokenSilently()
      .then((token) => {
        fetchDataAgnostic(
          'department/season/scale',
          { pk_Department: dContext.department.pk_Department, pk_Season: dContext.season.pk_Season },
          formatBearerToken(token),
        )
          .then((res) => {
            setIsUpdatingOptions(false);
            if (res.data) {
              if (res.data.QuestionOptions) {
                setQuestionChoices(res.data.QuestionOptions);
                setEditableQuestionChoices(res.data.QuestionOptions);
              }
              setQuestionScale(res.data.ScaleType || questionScaleKeys.likert5);
              setScaleData(res.data.ScaleType || questionScaleKeys.likert5);
            }
          })
          .catch((err) => {
            setIsUpdatingOptions(false);
          });
      })
      .catch((err) => {
        setIsUpdatingOptions(false);
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const checkScaleMatch = (term) => {
    if (questionScale === term) return true;

    return false;
  };

  // Updates local editable question list. editable question list compared with
  // reference list to see changes
  const updateQuestion = (index, key, value) => {
    const newQuestionList = clone(editableQuestionChoices);
    const newItem = clone(newQuestionList[index]);
    newItem[key] = value;
    // newItem['DisplayValue'] = '1'; // Forced to fix the null value currently.

    newQuestionList[index] = newItem;
    setEditableQuestionChoices(newQuestionList);
  };

  const saveScaleType = (type) => {
    setIsUpdatingScaleType(true);
    getTokenSilently()
      .then((token) => {
        putData(
          'department/season/scale',
          { pk_Department: dContext.department.pk_Department, pk_Season: dContext.season.pk_Season },
          {
            ScaleType: type,
          },
          formatBearerToken(token),
        )
          .then((res) => {
            setScaleHasChange(false);
            setIsUpdatingScaleType(false);
            getScaleData();

            toast.success('Scale Type saved!');
          })
          .catch((err) => {
            setIsUpdatingScaleType(false);
            toast.error('Error saving scale type!');
          });
      })
      .catch((err) => {
        setIsUpdatingScaleType(false);
        toast.error('Error saving scale type!');
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const saveQuestionChoices = (callbacks) => {
    const { resolve, reject } = callbacks || {};

    editableQuestionChoices.forEach((eqc) => {
      delete eqc.ScaleType;
      delete eqc.pk_QuestionOptionGroup;
    });
    setIsUpdatingOptions(true);
    getTokenSilently()
      .then((token) => {
        putData(
          'department/season/scale/options',
          { pk_Department: dContext.department.pk_Department, pk_Season: dContext.season.pk_Season },
          {
            payload: editableQuestionChoices,
          },
          formatBearerToken(token),
        )
          .then((res) => {
            setQuestionsHaveChange(false);
            if (resolve) {
              resolve();
            }
            setIsUpdatingOptions(false);
            setHasChange(false);
            toast.success('Saved new Default options!');
          })
          .catch((err) => {
            if (reject) {
              reject();
            }
            setIsUpdatingOptions(false);
            toast.error('Error saving new Default options!');
          });
      })
      .catch((err) => {
        setIsUpdatingOptions(false);
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const updateNonQuestions = (keyValues) => {
    const seasonObject = {};
    Object.keys(keyValues).forEach((key) => {
      seasonObject[key] = keyValues[key];
    });

    // AllowPrescreen: prescreenState ? 1 : 0,
    // EnablePrescreenQuestionnaire: enablePreScreenQuestionaire,
    // EnableEvaluationQuestionnaire: enableEvaluationQuestionaire,
    // EnablePrescreenMode: prescreenMode,

    setIsUpdatingQuestionnaires(true);
    getTokenSilently()
      .then((token) => {
        putData(
          'department/season',
          { pk_Department: dContext.department.pk_Department, pk_Season: dContext.season.pk_Season },
          {
            seasonObject,
          },
          formatBearerToken(token),
        )
          .then((res) => {
            setIsUpdatingQuestionnaires(false);
            setSeason(res.data);
            // updateLocalBooleans(res.data);
            getSeasonSettings();
            toast.success('Settings Updated!');
          })
          .catch((err) => {
            setIsUpdatingQuestionnaires(false);
            toast.error('Settings Update Failed!');
          });
      })
      .catch((err) => {
        toast.error('Settings Update Failed!');
        setIsUpdatingQuestionnaires(false);
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const renderManageFocuses = () => {
    return <Focuses dContext={dContext} updateChanges={updateChanges} windowWidth={windowWidth} />;
  };

  // Renders the three header cards responsively
  const renderHeaderCards = () => {
    // Responsive: column if windowWidth < widthToSwitchToMobile, else row
    const isMobile = windowWidth < widthToSwitchToMobile;
    // For row: 3 cards, each calc(33% - 5px), margin-right: 7.5px on first two, 0 on last
    // For column: 100% width, margin-bottom: 10px except last
    if (ProgramType === 'Mock Oral Board') {
      // Only Passing Score and Evaluation Questionnaire
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            width: '100%',
            paddingLeft: 15,
            paddingRight: 15,
            marginBottom: 30,
            alignItems: 'flex-start',
            justifyContent: isMobile ? 'flex-start' : 'space-between',
          }}
        >
          <div
            style={{
              width: isMobile ? '100%' : 'calc(50% - 7.5px)',
              marginRight: isMobile ? 0 : 15,
              marginBottom: isMobile ? 10 : 0,
            }}
          >
            <Card className="shadow">
              <CardHeader>
                <b>Passing Score</b>
              </CardHeader>
              <CardBody>
                <div style={style.simpleRow}>
                  <div style={{ ...style.simpleColumn }}>
                    <Input
                      type="number"
                      disabled={isUpdatingQuestionnaire}
                      value={PassingScore}
                      invalid={
                        !isUpdatingQuestionnaire && (PassingScore < 1 || PassingScore > editableQuestionChoices.length)
                      }
                      onChange={(e) => {
                        let newValue = parseFloat(e.target.value);
                        if (newValue > editableQuestionChoices.length) {
                          newValue = editableQuestionChoices.length;
                        } else if (newValue < 1) {
                          newValue = 1;
                        }
                        setPassingScore(roundToDecimalPlace(newValue, 2));
                      }}
                    />
                    <FormFeedback>Passing Score is greater than the max score!</FormFeedback>
                  </div>
                  <div style={{ ...style.simpleColumn, paddingLeft: 10 }}>
                    <Button
                      disabled={
                        isUpdatingQuestionnaire ||
                        (rawSeasonSettings && PassingScore === rawSeasonSettings.PassingScore)
                      }
                      color={'success'}
                      onClick={() => {
                        updateNonQuestions({ PassingScore });
                      }}
                    >
                      {' '}
                      Save{' '}
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
          <div
            style={{
              width: isMobile ? '100%' : 'calc(50% - 7.5px)',
              marginRight: 0,
              marginBottom: isMobile ? 0 : 0,
            }}
          >
            <Card className="shadow">
              <CardHeader>
                <b>Evaluation Questionnaire</b>
              </CardHeader>
              <CardBody>
                <div style={style.spaceBetweenRow}>
                  <div style={{ ...style.simpleColumn }}>
                    <FormGroup switch>
                      <Input
                        disabled={isUpdatingQuestionnaire}
                        id="evaluationEnabled"
                        type="switch"
                        role="switch"
                        checked={enableEvaluationQuestionaire}
                        onChange={(e) => {
                          const newValue = !enableEvaluationQuestionaire;
                          setEnableEvaluationQuestionaire(newValue);
                          updateNonQuestions({ EnableEvaluationQuestionnaire: newValue });
                        }}
                      />
                      <Label check htmlFor="evaluationEnabled">
                        Enable for Evaluators
                      </Label>
                    </FormGroup>
                  </div>
                  <div style={{ ...style.simpleColumn }}>
                    <FormGroup check>
                      <Input
                        disabled={isUpdatingQuestionnaire}
                        id="defaultEvaluation"
                        type="checkbox"
                        checked={!prescreenMode || !prescreenState}
                        onChange={(e) => {
                          if (prescreenMode) {
                            setPrescreenMode(false);
                            updateNonQuestions({ EnablePrescreenMode: false });
                          }
                        }}
                      />
                      <Label check htmlFor="defaultEvaluation">
                        Default
                      </Label>
                    </FormGroup>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      );
    }
    // Not Mock Oral Board: all three cards
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          width: '100%',
          paddingLeft: 15,
          paddingRight: 15,
          marginBottom: 30,
          alignItems: isMobile ? 'flex-start' : 'stretch',
          justifyContent: isMobile ? 'flex-start' : 'space-between',
        }}
      >
        <div
          style={{
            width: isMobile ? '100%' : 'calc(33% - 5px)',
            marginRight: isMobile ? 0 : 7.5,
            marginBottom: isMobile ? 10 : 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Card className="shadow" style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <CardHeader>
              <b>Prescreen with RezRATE</b>
            </CardHeader>
            <CardBody style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={style.spaceBetweenRow}>
                <div style={{ ...style.simpleColumn }}>
                  <FormGroup check>
                    <Input
                      disabled={isUpdatingQuestionnaire}
                      id="prescreenCandidates"
                      name="prescreenOption"
                      type="radio"
                      checked={prescreenState}
                      onChange={(e) => {
                        setPrescreenState(true);
                        updateNonQuestions({ AllowPrescreen: 1 });
                      }}
                    />
                    <Label check htmlFor="prescreenCandidates">
                      Prescreen Candidates
                    </Label>
                  </FormGroup>
                </div>
                <div style={{ ...style.simpleColumn }}>
                  <FormGroup check>
                    <Input
                      disabled={isUpdatingQuestionnaire}
                      id="doNotPrescreen"
                      name="prescreenOption"
                      type="radio"
                      checked={!prescreenState}
                      onChange={(e) => {
                        setPrescreenState(false);
                        const toUpdate = {};
                        toUpdate.AllowPrescreen = 0;
                        updateNonQuestions(toUpdate);
                      }}
                    />
                    <Label check htmlFor="doNotPrescreen">
                      Do not Prescreen Candidates
                    </Label>
                  </FormGroup>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
        <div
          style={{
            width: isMobile ? '100%' : 'calc(33% - 5px)',
            marginRight: isMobile ? 0 : 7.5,
            marginBottom: isMobile ? 10 : 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Card className="shadow" style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <CardHeader>
              <b>Prescreen Questionnaire</b>
            </CardHeader>
            <CardBody style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1 }}>
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 10 }}>
                  <FormGroup switch>
                    <Input
                      disabled={isUpdatingQuestionnaire || !prescreenState}
                      id="prescreenEnabled"
                      type="switch"
                      role="switch"
                      checked={enablePreScreenQuestionaire && prescreenState}
                      onChange={(e) => {
                        const newValue = !enablePreScreenQuestionaire;
                        setEnablePreScreenQuestionaire(newValue);
                        const toUpdate = {};
                        toUpdate.EnablePrescreenQuestionnaire = newValue;
                        updateNonQuestions(toUpdate);
                      }}
                    />
                    <Label check htmlFor="prescreenEnabled">
                      Enable for Evaluators
                    </Label>
                  </FormGroup>
                  <FormGroup switch>
                    <Input
                      disabled={isUpdatingQuestionnaire || !prescreenState}
                      id="prescreenResultsEnabled"
                      type="switch"
                      role="switch"
                      checked={enablePrescreenResultsToEvaluators && prescreenState}
                      onChange={(e) => {
                        const newValue = !enablePrescreenResultsToEvaluators;
                        setEnablePrescreenResultsToEvaluators(newValue);
                        const toUpdate = {};
                        toUpdate.EnablePrescreenVisibleToAllEvaluators = newValue;
                        updateNonQuestions(toUpdate);
                      }}
                    />
                    <Label check htmlFor="prescreenResultsEnabled">
                      Prescreen Results Visible to ALL Evaluators
                    </Label>
                  </FormGroup>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minWidth: 90,
                  }}
                >
                  <FormGroup check>
                    <Input
                      disabled={isUpdatingQuestionnaire || !prescreenState}
                      id="defaultPrescreen"
                      type="checkbox"
                      checked={prescreenMode && prescreenState}
                      onChange={(e) => {
                        if (!prescreenMode) {
                          setPrescreenMode(true);
                          updateNonQuestions({ EnablePrescreenMode: true });
                        }
                      }}
                    />
                    <Label check htmlFor="defaultPrescreen">
                      Default
                    </Label>
                  </FormGroup>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
        <div
          style={{
            width: isMobile ? '100%' : 'calc(33% - 5px)',
            marginRight: 0,
            marginBottom: isMobile ? 0 : 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Card className="shadow" style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <CardHeader>
              <b>Evaluation Questionnaire</b>
            </CardHeader>
            <CardBody style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={style.spaceBetweenRow}>
                <div style={{ ...style.simpleColumn }}>
                  <FormGroup switch>
                    <Input
                      disabled={isUpdatingQuestionnaire}
                      id="evaluationEnabledFull"
                      type="switch"
                      role="switch"
                      checked={enableEvaluationQuestionaire}
                      onChange={(e) => {
                        const newValue = !enableEvaluationQuestionaire;
                        setEnableEvaluationQuestionaire(newValue);
                        updateNonQuestions({ EnableEvaluationQuestionnaire: newValue });
                      }}
                    />
                    <Label check htmlFor="evaluationEnabledFull">
                      Enable for Evaluators
                    </Label>
                  </FormGroup>
                </div>
                <div style={{ ...style.simpleColumn }}>
                  <FormGroup check>
                    <Input
                      disabled={isUpdatingQuestionnaire}
                      id="defaultEvaluationFull"
                      type="checkbox"
                      checked={!prescreenMode || !prescreenState}
                      onChange={(e) => {
                        if (prescreenMode) {
                          setPrescreenMode(false);
                          updateNonQuestions({ EnablePrescreenMode: false });
                        }
                      }}
                    />
                    <Label check htmlFor="defaultEvaluationFull">
                      Default
                    </Label>
                  </FormGroup>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'row', overflowY: 'scroll', height: 'calc(100vh - 320px)' }}>
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <div style={{ ...style.simpleRow, paddingLeft: 15, paddingBottom: 20 }}>
          <div style={{ ...style.simpleColumn, fontSize: 22, width: '75%', fontWeight: 'bold' }}>
            Season: {season && season.SeasonName ? season.SeasonName : '---'}
          </div>
        </div>
        {renderHeaderCards()}
        <div style={{ marginBottom: 30 }}>
          <AdminEvaluationQuestionTemplate
            questionOptions={editableQuestionChoices.slice()}
            questionScale={questionScale}
            scaleData={scaleData}
            saveScaleType={saveScaleType}
            updateQuestion={updateQuestion}
            isUpdatingOptions={isUpdatingOptions}
            isUpdatingScaleType={isUpdatingScaleType}
            setQuestionScale={setQuestionScale}
            checkScaleMatch={checkScaleMatch}
            saveQuestionChoices={saveQuestionChoices}
            questionsHaveChange={questionsHaveChange}
            windowWidth={windowWidth}
          />
        </div>
        <div style={{ ...style.simpleRow, paddingLeft: 15, paddingBottom: 20 }}>
          <div style={{ ...style.simpleColumn, width: '100%' }}>
            <Card className="shadow">
              <CardHeader>
                {' '}
                <b>Manage Evaluation Focuses</b>
              </CardHeader>
              <CardBody>{renderManageFocuses()}</CardBody>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminEvluationSettingsLanding;

//  swap likert 10 and dryefus 10 buttons
